<script setup lang="ts">
import { computed, ref } from 'vue'
import CopyButton from '@/components/CopyButton.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import FeedbackButtons from '@/components/FeedbackButtons.vue'

const props = withDefaults(defineProps<{
  title: string
  description?: string
  guideline?: string
  traceId?: string
}>(), {
  description: '',
  guideline: '',
  traceId: '',
})

const TRUNCATED_DESCRIPTION_LENGTH = 175

const isExpanded = ref(false)

const isTruncatable = computed(() => props.description.length > TRUNCATED_DESCRIPTION_LENGTH)
const isShowingFullDescription = computed(() => isExpanded.value || !isTruncatable.value)

const displayedDescription = computed(() => {
  if (isShowingFullDescription.value) {
    return props.description
  }
  return `${props.description.substring(0, TRUNCATED_DESCRIPTION_LENGTH)} ...`
})

const expandToggleLabel = computed(() => (isExpanded.value ? 'see less' : 'read more'))
const copyText = computed(() => `${props.title}\n\n${props.description}`)

const feedbackEventData = computed(() => ({
  sdi_strategy_description: props.description,
  sdi_udl_guideline: props.guideline,
  sdi_strategy_title: props.title,
  sdi_trace: props.traceId,
}))

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}
</script>

<template>
  <CopyTrackWrapper type="sdi_strategy" :title="title" class="mb-4 last:mb-0">
    <div>
      <h4 class="inline text-xl font-bold">{{ title }}</h4>
      <CopyButton
        :string-to-copy="copyText"
        button-text=""
        button-theme="grayTertiary"
        size="xs"
        :aria-label="`Copy ${title} strategy`"
        tooltip-text="Copied Strategy"
        :auto-hide-tooltip="true"
      >
        <template #icon>
          <span class="material-icons-outlined text-lg" aria-hidden="true">content_copy</span>
        </template>
      </CopyButton>
    </div>
    <p class="mb-3 text-xl leading-7" data-testid="sdi-strategy-description">
      {{ displayedDescription }}
      <button
        v-if="isTruncatable"
        type="button"
        class="text-blue-400 hover:underline"
        :aria-expanded="isExpanded"
        @click="toggleExpanded"
      >
        {{ expandToggleLabel }}
      </button>
    </p>
    <FeedbackButtons :event-data="feedbackEventData" />
  </CopyTrackWrapper>
</template>
